/*
 * Copyright (c) 2024-2026, s0up and the autobrr contributors.
 * SPDX-License-Identifier: GPL-2.0-or-later
 */

import React from "react";
import { HardwareStats } from "@/api/monitor";
import { SystemDetail, PSIWindow } from "@/api/serverDetail";
import { DetailRates, HistoryPoint } from "@/hooks/useServerDetail";
import {
  Bar,
  KV,
  Meter,
  Panel,
  Spark,
  Tag,
  fmtBits,
  fmtBytes,
  fmtDuration,
  fmtPct,
  fmtRate,
  level,
  levelClass,
} from "./kit";

const SIGNAL = "var(--gh-signal)";
const WARN = "var(--gh-warn)";
const DIM = "var(--gh-dim)";

// ---------------- CPU ----------------

export const CpuPanel: React.FC<{ d: SystemDetail; hw?: HardwareStats; history: HistoryPoint[] }> = ({
  d,
  hw,
  history,
}) => {
  const cpu = d.cpu;
  const threads = cpu.threads || (cpu.per_core?.length ?? 0);
  const hint = hw?.cpu.model
    ? `${hw.cpu.model}${hw.cpu.frequency && !/GHz|MHz/i.test(hw.cpu.model) ? ` @ ${(hw.cpu.frequency / 1000).toFixed(2)} GHz` : ""}`
    : `${threads} threads`;
  return (
    <Panel title="CPU" hint={hint} className="sv-c8" defaultOpen>
      <div className="sv-bigrow">
        <span className={`sv-big ${levelClass(level(cpu.total))}`}>
          {cpu.total.toFixed(0)}
          <small>%</small>
        </span>
        <span className="sv-note">
          user {cpu.user.toFixed(0)}% · system {cpu.system.toFixed(0)}% · iowait {cpu.iowait.toFixed(1)}%
          {cpu.steal > 0.05 ? ` · steal ${cpu.steal.toFixed(1)}%` : ""}
        </span>
      </div>
      <Spark
        series={[
          { values: history.map((h) => h.cpu), color: SIGNAL, label: "cpu" },
          { values: history.map((h) => h.iowait), color: WARN, label: "iowait" },
        ]}
        max={100}
        format={(v) => `${v.toFixed(0)}%`}
      />
      <div className="sv-cores">
        {(cpu.per_core ?? []).map((v, i) => {
          const l = level(v);
          return (
            <div className="sv-core" key={i} title={`core ${i}: ${v.toFixed(0)}%`}>
              <div className="sv-core-bar">
                <i className={l ? `gh-bar--${l}` : ""} style={{ height: `${Math.max(2, v)}%` }} />
              </div>
              <span>{v.toFixed(0)}</span>
            </div>
          );
        })}
      </div>
      <div className="sv-2col">
        <KV k="Load average">
          <span className={levelClass(level((cpu.load5 / Math.max(1, threads)) * 100, 100, 150))}>
            {cpu.load1.toFixed(2)} · {cpu.load5.toFixed(2)} · {cpu.load15.toFixed(2)}
          </span>
        </KV>
        <KV k="Threads">{threads}</KV>
      </div>
    </Panel>
  );
};

// ---------------- memory ----------------

export const MemoryPanel: React.FC<{ hw?: HardwareStats }> = ({ hw }) => {
  const m = hw?.memory;
  return (
    <Panel title="Memory" hint={m ? fmtBytes(m.total) : undefined} className="sv-c4" defaultOpen>
      {!m ? (
        <div className="sv-empty">no data</div>
      ) : (
        <>
          <Meter
            label="RAM"
            value={fmtPct(m.used_percent)}
            pct={m.used_percent}
            sub={`${fmtBytes(m.used)} used · ${fmtBytes(m.available)} available`}
            warn={80}
            crit={92}
          />
          {m.swap_total > 0 && (
            <Meter
              label="Swap"
              value={fmtPct(m.swap_percent)}
              pct={m.swap_percent}
              sub={`${fmtBytes(m.swap_used)} of ${fmtBytes(m.swap_total)}`}
              warn={40}
              crit={70}
            />
          )}
          {m.zfs_arc > 0 && (
            <Meter label="ZFS ARC" value={fmtBytes(m.zfs_arc)} pct={(m.zfs_arc / m.total) * 100} sub="reclaimable cache" warn={101} crit={101} />
          )}
          <div className="sv-kv">
            <KV k="Cached">{fmtBytes(m.cached)}</KV>
            <KV k="Buffers">{fmtBytes(m.buffers)}</KV>
            <KV k="Free">{fmtBytes(m.free)}</KV>
          </div>
        </>
      )}
    </Panel>
  );
};

// ---------------- pressure ----------------

const PsiRow: React.FC<{ label: string; w: PSIWindow; warn: number; crit: number }> = ({ label, w, warn, crit }) => (
  <Meter
    label={label}
    value={`${w.avg10.toFixed(1)}%`}
    pct={Math.min(100, w.avg60 * 2)}
    sub={`10s ${w.avg10.toFixed(1)} · 60s ${w.avg60.toFixed(1)} · 5m ${w.avg300.toFixed(1)}`}
    warn={warn}
    crit={crit}
  />
);

export const PressurePanel: React.FC<{ d: SystemDetail }> = ({ d }) => {
  const p = d.pressure;
  if (!p) return null;
  return (
    <Panel title="Pressure (PSI)" hint="time tasks were stalled" className="sv-c4">
      <PsiRow label="CPU waiting" w={p.cpu_some} warn={60} crit={90} />
      <PsiRow label="Memory waiting" w={p.mem_some} warn={20} crit={60} />
      <PsiRow label="Memory stalled" w={p.mem_full} warn={10} crit={40} />
      <PsiRow label="I/O waiting" w={p.io_some} warn={40} crit={80} />
      <PsiRow label="I/O stalled" w={p.io_full} warn={30} crit={70} />
    </Panel>
  );
};

// ---------------- network ----------------

export const NetworkPanel: React.FC<{ d: SystemDetail; rates: DetailRates; history: HistoryPoint[] }> = ({
  d,
  rates,
  history,
}) => {
  const nics = (d.nics ?? []).filter((n) => n.up || n.rx_bytes > 0);
  return (
    <Panel title="Network" hint="live, from interface counters" className="sv-c8" defaultOpen>
      <div className="sv-bigrow">
        <span className="sv-big sv-ok">
          ↓ {fmtRate(rates.netRx)}
        </span>
        <span className="sv-big" style={{ color: DIM }}>
          ↑ {fmtRate(rates.netTx)}
        </span>
        <span className="sv-note">
          {fmtBits(rates.netRx)} in · {fmtBits(rates.netTx)} out
        </span>
      </div>
      <Spark
        series={[
          { values: history.map((h) => h.rx), color: SIGNAL, label: "down" },
          { values: history.map((h) => h.tx), color: DIM, label: "up" },
        ]}
        format={fmtRate}
      />
      {nics.length > 0 && (
        <div className="sv-tablewrap">
          <table className="sv-table">
            <thead>
              <tr>
                <th>Interface</th>
                <th>Link</th>
                <th className="n">Down</th>
                <th className="n">Up</th>
                <th className="n">Total in</th>
                <th className="n">Total out</th>
                <th className="n">Err / drop</th>
              </tr>
            </thead>
            <tbody>
              {nics.map((n) => {
                const r = rates.nics[n.name];
                const errs = n.rx_errors + n.tx_errors;
                const drops = n.rx_dropped + n.tx_dropped;
                return (
                  <tr key={n.name}>
                    <td className="name">
                      {n.name}
                      <small>{n.addrs?.find((a) => !a.includes(":")) ?? n.mac ?? ""}</small>
                    </td>
                    <td>
                      <Tag tone={n.up ? "ok" : "crit"}>{n.up ? "up" : "down"}</Tag>
                      {n.speed_mbps ? <span className="sv-mute sv-mono"> {n.speed_mbps >= 1000 ? `${n.speed_mbps / 1000}G` : `${n.speed_mbps}M`}</span> : null}
                    </td>
                    <td className="n">{r ? fmtRate(r.rx) : "-"}</td>
                    <td className="n">{r ? fmtRate(r.tx) : "-"}</td>
                    <td className="n">{fmtBytes(n.rx_bytes)}</td>
                    <td className="n">{fmtBytes(n.tx_bytes)}</td>
                    <td className={`n ${errs > 0 ? "sv-warn" : ""}`}>
                      {errs} / {drops}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Panel>
  );
};

// ---------------- storage ----------------

export const FilesystemPanel: React.FC<{ hw?: HardwareStats }> = ({ hw }) => (
  <Panel title="Filesystems" hint={hw ? `${(hw.disks?.length ?? 0)} mounts` : undefined} className="sv-c4">
    {!hw || (hw.disks?.length ?? 0) === 0 ? (
      <div className="sv-empty">no data</div>
    ) : (
      <div className="sv-scroll">
      {(hw.disks ?? []).map((f) => (
        <Meter
          key={f.path + f.device}
          label={f.path}
          value={fmtPct(f.used_percent)}
          pct={f.used_percent}
          sub={`${fmtBytes(f.used)} of ${fmtBytes(f.total)} · ${f.fstype}`}
          warn={80}
          crit={92}
        />
      ))}
      </div>
    )}
  </Panel>
);

export const DiskIoPanel: React.FC<{ d: SystemDetail; rates: DetailRates; history: HistoryPoint[] }> = ({
  d,
  rates,
  history,
}) => {
  const disks = d.disk_io ?? [];
  if (disks.length === 0) return null;
  return (
    <Panel title="Disk I/O" hint="per device" className="sv-c8">
      <div className="sv-bigrow">
        <span className="sv-big sv-ok">R {fmtRate(rates.diskRead)}</span>
        <span className="sv-big" style={{ color: DIM }}>
          W {fmtRate(rates.diskWrite)}
        </span>
      </div>
      <Spark
        series={[
          { values: history.map((h) => h.read), color: SIGNAL, label: "read" },
          { values: history.map((h) => h.write), color: WARN, label: "write" },
        ]}
        format={fmtRate}
      />
      <div className="sv-tablewrap">
        <table className="sv-table">
          <thead>
            <tr>
              <th>Device</th>
              <th className="n">Read</th>
              <th className="n">Write</th>
              <th style={{ width: 120 }}>Busy</th>
              <th className="n">Total read</th>
              <th className="n">Total written</th>
            </tr>
          </thead>
          <tbody>
            {disks.map((x) => {
              const r = rates.disks[x.name];
              return (
                <tr key={x.name}>
                  <td className="name">{x.name}</td>
                  <td className="n">{r ? fmtRate(r.read) : "-"}</td>
                  <td className="n">{r ? fmtRate(r.write) : "-"}</td>
                  <td>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 34px", gap: 8, alignItems: "center" }}>
                      <Bar pct={r?.busy ?? 0} warn={60} crit={90} />
                      <span className="sv-mono sv-mute" style={{ fontSize: 11, textAlign: "right" }}>
                        {r ? `${r.busy.toFixed(0)}%` : "-"}
                      </span>
                    </div>
                  </td>
                  <td className="n">{fmtBytes(x.read_bytes)}</td>
                  <td className="n">{fmtBytes(x.write_bytes)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

export const SmartPanel: React.FC<{ d: SystemDetail }> = ({ d }) => {
  const drives = d.smart ?? [];
  if (drives.length === 0) return null;
  return (
    <Panel title="Drive health (SMART)" hint={`${drives.length} drives`} className="sv-c12">
      <div className="sv-tablewrap">
        <table className="sv-table">
          <thead>
            <tr>
              <th>Drive</th>
              <th>Health</th>
              <th className="n">Temp</th>
              <th className="n">Power-on</th>
              <th className="n">Cycles</th>
              <th className="n">Realloc</th>
              <th className="n">Pending</th>
              <th className="n">CRC</th>
              <th className="n">SSD wear</th>
              <th className="n">Size</th>
            </tr>
          </thead>
          <tbody>
            {drives.map((s) => {
              const tone = s.health === "passed" ? "ok" : s.health === "failed" ? "crit" : "";
              const bad = (n?: number) => (n ?? 0) > 0;
              return (
                <tr key={s.device}>
                  <td className="name">
                    {s.model || s.device}
                    <small>
                      {s.device}
                      {s.serial ? ` · ${s.serial}` : ""} · {s.protocol === "NVMe" ? "NVMe" : s.rotational ? "HDD" : "SSD"}
                    </small>
                  </td>
                  <td>
                    <Tag tone={tone}>{s.health}</Tag>
                  </td>
                  <td className={`n ${(s.temp_c ?? 0) >= (s.rotational ? 55 : 70) ? "sv-warn" : ""}`}>
                    {s.temp_c ? `${s.temp_c.toFixed(0)} °C` : "-"}
                  </td>
                  <td className="n">{s.power_on_hours ? fmtDuration(s.power_on_hours * 3600) : "-"}</td>
                  <td className="n">{s.power_cycles ?? "-"}</td>
                  <td className={`n ${bad(s.reallocated) ? "sv-warn" : ""}`}>{s.health === "standby" ? "-" : s.reallocated ?? 0}</td>
                  <td className={`n ${bad(s.pending) ? "sv-warn" : ""}`}>{s.health === "standby" ? "-" : s.pending ?? 0}</td>
                  <td className={`n ${bad(s.crc_errors) ? "sv-warn" : ""}`}>{s.health === "standby" ? "-" : s.crc_errors ?? 0}</td>
                  <td className={`n ${(s.wear_percent_used ?? 0) >= 80 ? "sv-warn" : ""}`}>
                    {s.wear_percent_used ? `${s.wear_percent_used}%` : "-"}
                  </td>
                  <td className="n">{s.capacity_bytes ? fmtBytes(s.capacity_bytes, 0) : "-"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

export const ZfsPanel: React.FC<{ d: SystemDetail }> = ({ d }) => {
  const pools = d.zfs ?? [];
  if (pools.length === 0) return null;
  return (
    <Panel title="ZFS pools" hint={`${pools.length} pools`} className="sv-c8">
      {pools.map((p) => {
        const tone = p.health === "ONLINE" ? "ok" : p.health === "DEGRADED" ? "warn" : "crit";
        return (
          <div className="sv-pool" key={p.name}>
            <div className="sv-pool-head">
              <span className="sv-pool-name">{p.name}</span>
              <Tag tone={tone}>{p.health}</Tag>
              <span className="sv-mono sv-mute" style={{ fontSize: 11, marginLeft: "auto" }}>
                {fmtBytes(p.alloc_bytes)} / {fmtBytes(p.size_bytes)} · frag {p.frag_percent}%
              </span>
            </div>
            <Bar pct={p.cap_percent} warn={80} crit={92} />
            {(p.devices?.length ?? 0) > 0 && (
              <div className="sv-vdevs">
                <div className="sv-vdev sv-mute">
                  <span>device</span>
                  <span>state</span>
                  <span>R</span>
                  <span>W</span>
                  <span>C</span>
                </div>
                {p.devices!.map((v, i) => (
                  <div className="sv-vdev" key={i}>
                    <span style={{ paddingLeft: v.depth * 12 }} title={v.name}>
                      {v.name}
                    </span>
                    <span className={v.state === "ONLINE" ? "sv-ok" : v.state === "DEGRADED" ? "sv-warn" : "sv-crit"}>{v.state}</span>
                    <span>{v.read}</span>
                    <span>{v.write}</span>
                    <span>{v.cksum}</span>
                  </div>
                ))}
              </div>
            )}
            {p.scan && <div className="sv-pool-note">scan: {p.scan}</div>}
            {p.status && <div className="sv-pool-note sv-warn">{p.status}</div>}
            {p.action && <div className="sv-pool-note">{p.action}</div>}
            {p.errors && <div className={`sv-pool-note ${p.errors === "No known data errors" ? "" : "sv-crit"}`}>{p.errors}</div>}
          </div>
        );
      })}
    </Panel>
  );
};

// ---------------- thermal / power ----------------

export const ThermalPanel: React.FC<{ hw?: HardwareStats; d: SystemDetail }> = ({ hw, d }) => {
  const temps = [...(hw?.temperature ?? [])].sort((a, b) => b.temperature - a.temperature).slice(0, 10);
  const drives = (d.smart ?? []).filter((s) => s.temp_c);
  if (temps.length === 0 && drives.length === 0) return null;
  return (
    <Panel title="Temperatures" hint={temps[0] ? `max ${temps[0].temperature.toFixed(0)} °C` : undefined} className="sv-c8">
      {temps.map((t, i) => {
        const limit = t.critical && t.critical > 0 ? t.critical : 100;
        return (
          <Meter
            key={i}
            label={t.label || t.sensor_key}
            value={`${t.temperature.toFixed(0)} °C`}
            pct={(t.temperature / limit) * 100}
            warn={80}
            crit={92}
          />
        );
      })}
      {drives.map((s) => (
        <Meter
          key={s.device}
          label={`${s.model ?? s.device}`}
          value={`${s.temp_c!.toFixed(0)} °C`}
          pct={(s.temp_c! / (s.rotational ? 60 : 80)) * 100}
          warn={85}
          crit={100}
        />
      ))}
    </Panel>
  );
};

export const UpsPanel: React.FC<{ d: SystemDetail }> = ({ d }) => {
  const u = d.ups;
  if (!u) return null;
  const onBattery = u.status.split(/\s+/).includes("OB");
  return (
    <Panel title="UPS" hint={u.model} className="sv-c4">
      <div className="sv-bigrow">
        <span className={`sv-big ${onBattery ? "sv-crit" : "sv-ok"}`}>{u.status_text || u.status}</span>
      </div>
      <Meter label="Battery" value={`${u.charge_percent.toFixed(0)}%`} pct={100 - u.charge_percent} warn={50} crit={80} sub={`${fmtDuration(u.runtime_seconds)} runtime left`} />
      <Meter label="Load" value={`${u.load_percent.toFixed(0)}%`} pct={u.load_percent} warn={70} crit={90} />
      <div className="sv-kv">
        <KV k="Input">{u.input_voltage.toFixed(0)} V</KV>
        <KV k="Output">
          {u.output_voltage.toFixed(0)} V · {u.output_frequency.toFixed(1)} Hz
        </KV>
        <KV k="Battery voltage">{u.battery_voltage.toFixed(1)} V</KV>
      </div>
    </Panel>
  );
};

// ---------------- workloads ----------------

export const GuestsPanel: React.FC<{ d: SystemDetail }> = ({ d }) => {
  const guests = (d.guests ?? []).filter((g) => !g.template);
  if (guests.length === 0) return null;
  const running = guests.filter((g) => g.status === "running").length;
  return (
    <Panel title="Proxmox guests" hint={`${running}/${guests.length} running`} className="sv-c12">
      <div className="sv-tablewrap">
        <table className="sv-table">
          <thead>
            <tr>
              <th>Guest</th>
              <th>State</th>
              <th className="n">CPU</th>
              <th style={{ width: 170 }}>Memory</th>
              <th className="n">Disk</th>
              <th className="n">Net in / out</th>
              <th className="n">Uptime</th>
            </tr>
          </thead>
          <tbody>
            {guests.map((g) => {
              const up = g.status === "running";
              const memPct = g.maxmem ? (g.mem / g.maxmem) * 100 : 0;
              return (
                <tr key={g.id}>
                  <td className="name">
                    {g.name}
                    <small>
                      {g.type} {g.vmid}
                      {g.pool ? ` · ${g.pool}` : ""}
                    </small>
                  </td>
                  <td>
                    <Tag tone={up ? "ok" : ""}>{g.status}</Tag>
                  </td>
                  <td className="n">{up ? `${(g.cpu * 100).toFixed(1)}%` : "-"}</td>
                  <td>
                    {up ? (
                      <div style={{ display: "grid", gap: 3 }}>
                        <Bar pct={memPct} warn={80} crit={92} />
                        <span className="sv-mono sv-mute" style={{ fontSize: 10.5 }}>
                          {fmtBytes(g.mem)} / {fmtBytes(g.maxmem, 0)}
                        </span>
                      </div>
                    ) : (
                      <span className="sv-mute sv-mono">{fmtBytes(g.maxmem, 0)}</span>
                    )}
                  </td>
                  <td className="n">{g.maxdisk ? `${fmtBytes(g.disk)} / ${fmtBytes(g.maxdisk, 0)}` : "-"}</td>
                  <td className="n">{up ? `${fmtBytes(g.netin)} / ${fmtBytes(g.netout)}` : "-"}</td>
                  <td className="n">{up ? fmtDuration(g.uptime) : "-"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

export const DockerPanel: React.FC<{ d: SystemDetail }> = ({ d }) => {
  const list = d.docker ?? [];
  if (list.length === 0) return null;
  const running = list.filter((c) => c.state === "running").length;
  return (
    <Panel title="Docker containers" hint={`${running}/${list.length} running`} className="sv-c12">
      <div className="sv-tablewrap">
        <table className="sv-table">
          <thead>
            <tr>
              <th>Container</th>
              <th>State</th>
              <th>Image</th>
              <th className="n">CPU</th>
              <th className="n">Memory</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {list.map((c) => {
              const up = c.state === "running";
              const tone = up ? "ok" : c.state === "restarting" ? "crit" : c.state === "exited" ? "warn" : "";
              return (
                <tr key={c.name}>
                  <td className="name">{c.name}</td>
                  <td>
                    <Tag tone={tone}>{c.state}</Tag>
                  </td>
                  <td className="sv-mono sv-mute" style={{ maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis" }}>
                    {c.image}
                  </td>
                  <td className="n">{up ? `${c.cpu_percent.toFixed(1)}%` : "-"}</td>
                  <td className="n">{up ? fmtBytes(c.mem_bytes) : "-"}</td>
                  <td className="sv-mono sv-mute">{c.status}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};

export const ServicesPanel: React.FC<{ d: SystemDetail }> = ({ d }) => {
  const failed = d.failed_units ?? [];
  return (
    <Panel title="Services" hint={failed.length ? `${failed.length} failed` : "systemd"} className="sv-c4">
      {failed.length === 0 ? (
        <div className="sv-empty sv-ok">all units healthy</div>
      ) : (
        <div className="sv-kv">
          {failed.map((u) => (
            <div key={u.unit} style={{ display: "grid", gap: 2 }}>
              <span className="sv-warn sv-mono" style={{ fontSize: 12 }}>
                {u.unit}
              </span>
              {u.description && <span className="sv-note">{u.description}</span>}
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
};

export const ProcessesPanel: React.FC<{ d: SystemDetail }> = ({ d }) => {
  const procs = d.processes ?? [];
  if (procs.length === 0) return null;
  return (
    <Panel title="Top processes" hint={`${d.host.processes} running`} className="sv-c12">
      <div className="sv-tablewrap">
        <table className="sv-table">
          <thead>
            <tr>
              <th>Process</th>
              <th>User</th>
              <th className="n">PID</th>
              <th className="n">CPU</th>
              <th className="n">Memory</th>
            </tr>
          </thead>
          <tbody>
            {procs.map((p) => (
              <tr key={p.pid}>
                <td className="name">{p.name}</td>
                <td className="sv-mono sv-mute">{p.user ?? ""}</td>
                <td className="n">{p.pid}</td>
                <td className="n">{p.cpu_percent.toFixed(1)}%</td>
                <td className="n">
                  {fmtBytes(p.mem_bytes)} <span className="sv-mute">({p.mem_percent.toFixed(1)}%)</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );
};
